import { DestroyRef, Injectable, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslateService, TranslateStore } from "@ngx-translate/core";
import { CHATBOT_TRANSLATIONS, CHATBOT_TRANSLATION_NAMESPACE, ChatbotTranslation, ChatbotTranslations } from "../i18n/translations";

/**
 * Puts this widget's own strings into the translations the host already has, so that a host
 * carries none of them.
 *
 * **Underneath, never over.** Whatever the host declares under the same namespace wins, which is
 * how a product renames the assistant without forking anything; everything it has not declared
 * falls back to what ships here.
 *
 * It merges again on every language change because a host's loader **replaces** the translations
 * of the language it has just fetched: merging once at startup would last exactly until the first
 * one finished loading.
 */
@Injectable({ providedIn: "root" })
export class ChatbotTranslationsService {
    private readonly translate = inject(TranslateService, { optional: true });
    /**
     * Read from here and not through the service, because the service resolves a missing key
     * through the fallback language: asking it what the host says in Italian answers with the
     * English this library just installed, and merging that back in would leave every language
     * speaking English.
     */
    private readonly store = inject(TranslateStore, { optional: true });
    private readonly destroyRef = inject(DestroyRef);

    /**
     * Starts merging, and keeps merging.
     *
     * A host without `@ngx-translate` is left alone: this library asks for it as a peer, and one
     * that does not provide it sees the keys go untranslated, exactly as it would have before.
     */
    start(): void {
        const translate = this.translate;
        if (!translate) {
            return;
        }

        this.apply(translate.getCurrentLang());
        translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => this.apply(event.lang));
    }

    /**
     * One of this widget's own strings, right now.
     *
     * Asks the host first, so an override still wins, and falls back to the copy that ships here.
     * That fallback is not belt and braces: the host's loader hands its translations to whoever
     * asked for them before this service gets its turn to merge, so a string read in that instant
     * would come back as its own key. The template pipes never notice, since they render again
     * when the merge lands; a string read once from TypeScript would keep the key forever.
     */
    instant(key: string): string {
        const fromHost = this.translate?.instant(key);
        if (typeof fromHost === "string" && fromHost !== key) {
            return fromHost;
        }

        const lang = this.translate?.getCurrentLang();
        const shipped = lang ? read(CHATBOT_TRANSLATIONS[lang], key) : undefined;
        return shipped ?? (typeof fromHost === "string" ? fromHost : key);
    }

    private apply(lang: string | null | undefined): void {
        const translate = this.translate;
        if (!translate || !lang) {
            return;
        }

        const defaults = CHATBOT_TRANSLATIONS[lang];
        if (!defaults) {
            return;
        }

        // What the host itself says under this namespace for this language, if anything.
        const stored = this.store?.getTranslations(lang) as ChatbotTranslations | undefined;
        const existing = stored?.[CHATBOT_TRANSLATION_NAMESPACE];
        const fromHost = isGroup(existing) ? existing : {};

        translate.setTranslation(lang, { [CHATBOT_TRANSLATION_NAMESPACE]: mergeUnder(defaults, fromHost) }, true);
    }
}

/**
 * `base` with `over` on top, as deep as the groups go.
 *
 * Exported for its own tests: "the host wins" is a rule of this library, not a detail of how the
 * merging happens to be written.
 *
 * The strings are grouped (the sign in dialog is a group of its own), and a host overriding one
 * line of a group must not lose the rest of it, which is exactly what a shallow merge would do.
 */
export function mergeUnder(base: ChatbotTranslations, over: ChatbotTranslations): ChatbotTranslations {
    const merged: ChatbotTranslations = { ...base };

    for (const [key, value] of Object.entries(over)) {
        const beneath = merged[key];
        const bothGroups = isGroup(beneath) && isGroup(value);
        merged[key] = bothGroups ? mergeUnder(beneath, value) : value;
    }

    return merged;
}

/** Follows a dotted key, namespace included, through a group of strings. */
function read(translations: ChatbotTranslations | undefined, key: string): string | undefined {
    const path = key.split(".");
    if (path.shift() !== CHATBOT_TRANSLATION_NAMESPACE) {
        return undefined;
    }

    let value: ChatbotTranslation | undefined = translations;
    for (const step of path) {
        value = isGroup(value) ? value[step] : undefined;
    }
    return typeof value === "string" ? value : undefined;
}

function isGroup(value: unknown): value is ChatbotTranslations {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
